import type { EffectiveSettings } from "src/services/appSettingsService/appSettingsService";
import type { Settings } from "src/types/settings";

export const subscriptionPagePath = "/account/workspaces/subscription";

export type DashboardPageAccessContext = {
    effective: EffectiveSettings;
    pages: Settings["features"]["pages"];
};

const dashboardPathToAccessor = {
    "/account/workspaces": (ctx: DashboardPageAccessContext) => ctx.pages.workspacePageEnabled,
    "/dashboard/enrollment": (ctx: DashboardPageAccessContext) => ctx.effective.isEnrollmentPageEnabled,
    "/dashboard/devices": (ctx: DashboardPageAccessContext) => ctx.effective.isDevicesPageEnabled,
    "/dashboard/downloads": (ctx: DashboardPageAccessContext) => ctx.effective.isDownloadsPageEnabled,
    "/dashboard/files": (ctx: DashboardPageAccessContext) => ctx.effective.isFilesPageEnabled,
    "/dashboard/groups": (ctx: DashboardPageAccessContext) => ctx.effective.isGroupsPageEnabled,
    "/dashboard/licenses": (ctx: DashboardPageAccessContext) => ctx.effective.isUniversalLicensesPageEnabled,
    "/dashboard/overview": (ctx: DashboardPageAccessContext) => ctx.effective.isOverviewPageEnabled,
    "/dashboard/policies": (ctx: DashboardPageAccessContext) => ctx.pages.policiesPageEnabled,
    "/dashboard/products": (ctx: DashboardPageAccessContext) => ctx.effective.isProductsPageEnabled,
    "/dashboard/provisioning": (ctx: DashboardPageAccessContext) => ctx.effective.isProvisioningPageEnabled,
    "/dashboard/repositories": (ctx: DashboardPageAccessContext) => ctx.pages.repositoriesPageEnabled,
    "/dashboard/rollouts": (ctx: DashboardPageAccessContext) => ctx.effective.isRolloutPageEnabled,
} as const satisfies Record<string, (ctx: DashboardPageAccessContext) => boolean>;

type DashboardGuardPath = keyof typeof dashboardPathToAccessor;

function isPathUnderBase(pathname: string, base: string): boolean {
    return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Param routes under products/provisioning inherit the same page toggle as their base URLs.
 */
export function isDashboardPageAccessible(pathname: string, ctx: DashboardPageAccessContext): boolean | null {
    if (isPathUnderBase(pathname, "/dashboard/products")) {
        return dashboardPathToAccessor["/dashboard/products"](ctx);
    }
    if (isPathUnderBase(pathname, "/dashboard/provisioning")) {
        return dashboardPathToAccessor["/dashboard/provisioning"](ctx);
    }
    if (isPathUnderBase(pathname, "/account/workspaces")) {
        return dashboardPathToAccessor["/account/workspaces"](ctx);
    }
    if (pathname in dashboardPathToAccessor) {
        return dashboardPathToAccessor[pathname as DashboardGuardPath](ctx);
    }
    return null;
}

// Priority order for picking the default dashboard redirect. Mirrors the nav drawer order intentionally.
const firstAvailableDashboardPathOrder: readonly DashboardGuardPath[] = [
    "/dashboard/overview",
    "/dashboard/devices",
    "/dashboard/groups",
    "/dashboard/provisioning",
    "/dashboard/downloads",
    "/dashboard/products",
    "/dashboard/files",
    "/dashboard/rollouts",
    "/dashboard/repositories",
    "/dashboard/licenses",
    "/account/workspaces",
];

export function getFirstAvailableDashboardPath(ctx: DashboardPageAccessContext): DashboardGuardPath | null {
    for (const path of firstAvailableDashboardPathOrder) {
        if (dashboardPathToAccessor[path](ctx)) {
            return path;
        }
    }
    return null;
}

/**
 * The single decision point for "which page should the user land on".
 * PageGuard funnels every landing here (and relocates the user when the current page isn't it)
 * instead of picking a landing page itself.
 */
export function getRecommendedPathForUser(ctx: DashboardPageAccessContext): string {
    return getFirstAvailableDashboardPath(ctx) ?? "/forbidden";
}
